import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";

// --- Format helpers ---

const formatCurrency = (value) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatPercent = (value) => {
  if (value === null || value === undefined) return "ND";
  return new Intl.NumberFormat("it-IT", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
};

const fmtNum = (num) => {
  if (num === null || num === undefined) return "";
  return num.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (date) => {
  if (!date) return "";
  try {
    return format(new Date(date), "dd/MM/yyyy");
  } catch {
    return "";
  }
};

const fmtBool = (val) => (val ? "Sì" : "No");

// --- CSV Export ---

export const exportToCSV = (data, filename) => {
  // Use headers exactly as requested
  const headers = [
    "Collaboratore",
    "Cliente",
    "Targa",
    "Prodotto",
    "Tariffa",
    "Codice polizza anno precedente",
    "Codice polizza in scadenza",
    "Codice preventivo rinnovo",
    "Data transazione",
    "Decorrenza anno precedente",
    "Scadenza copertura",
    "Scadenza preventivo rinnovo",
    "Frazionamento polizza",
    "Causale",
    "Premio annuale in scadenza",
    "Premio annuale di rinnovo",
    "Delta annuale €",
    "Delta annuale %",
    "Premio rata in scadenza",
    "Premio rata di rinnovo",
    "Delta rata €",
    "Delta rata %",
    "Sconto commerciale",
    "Numero garanzie",
    "Commissioni totali",
    "Rischio",
    "Azione",
    "Metodo di pagamento",
    "Fascia aumento",
    "Severità alert",
    "Priorità intervento",
    "Presenza preventivo rinnovo",
    "Possibile rischio abbandono",
    "Stato lavorazione",
    "Note collaboratore",
  ];

  const rows = data.map((r) => [
    r.collaboratoreRinnovo,
    r.clienteRinnovo,
    r.riferimento, // Targa
    r.prodottoRinnovo || r.Prodotto,
    r.tariffa || r.Tariffa,
    r["Codice polizza anno precedente"] || "",
    r["Codice polizza in scadenza"] || r.codicePolizzaScadenza || "",
    r["Codice preventivo di rinnovo"] || r.codicePreventivoRinnovo || "",
    fmtDate(r["Data transazione"] || r.dataTransazione),
    fmtDate(r["Decorrenza anno precedente"] || r.decorrenzaPrecedente),
    fmtDate(r.scadenzaCopertura),
    fmtDate(r["Scadenza preventivo rinnovo"] || r.scadenzaPreventivoRinnovo),
    r.frazionamentoPolizza,
    r.causale,
    fmtNum(r.premioAnnualeScadenza),
    fmtNum(r.premioAnnualeRinnovo),
    fmtNum(r.deltaAssolutoCalcolato),
    fmtNum(r.deltaPercentualeCalcolato),
    fmtNum(r.premioRataScadenza),
    fmtNum(r.premioRataRinnovo),
    fmtNum(r.deltaRataAssoluto),
    fmtNum(r.deltaRataPercentuale),
    r["Sconto commerciale di rinnovo"] || r.scontoCommercialeRinnovo || "",
    r["Numero garanzie"] || r.numeroGaranzie || "",
    r["Commissioni totali"] || r.commissioniTotali || "",
    r.rischio || "",
    r.azione || r.azioneConsigliata || "",
    r["Metodo di pagamento"] || r.metodoPagamento || "",
    r.fasciaAumento,
    r.severitaAlert,
    r.prioritaIntervento,
    fmtBool(r.presenzaPreventivoRinnovo),
    fmtBool(r.possibileRischioAbbandono),
    "", // Stato lavorazione
    "", // Note collaboratore
  ]);

  const escape = (cell) => {
    if (cell === null || cell === undefined) return "";
    return `"${String(cell).replace(/"/g, '""')}"`;
  };

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map(escape).join(",")),
  ].join("\n");

  alert(
    `CSV generato correttamente.\n\nRighe esportate: ${rows.length}\nColonne incluse: ${headers.length}\n\nIl download inizierà a breve.`,
  );

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// --- PDF Report ---

export const generatePDFReport = (data, kpi) => {
  const doc = new jsPDF();
  const today = format(new Date(), "dd/MM/yyyy");

  // Title
  doc.setFontSize(20);
  doc.text("Report Analisi Rinnovi", 14, 22);
  doc.setFontSize(10);
  doc.text(`Generato il: ${today}`, 14, 28);

  // KPI Summary
  doc.setFontSize(14);
  doc.text("Sintesi KPI", 14, 40);

  const kpiData = [
    ["Totale Polizze in Scadenza", String(kpi.totalRenewals)],
    ["Premio Totale in Scadenza", formatCurrency(kpi.totalPremiumExpiring)],
    ["Premio Totale Rinnovo", formatCurrency(kpi.totalPremiumRenewal)],
    ["Delta Totale", formatCurrency(kpi.totalDeltaAbsolute)],
    ["Aumento Medio", formatPercent(kpi.avgDeltaPercent)],
    ["Polizze con Aumento > 20%", String(kpi.countIncrease20)],
  ];

  autoTable(doc, {
    startY: 45,
    head: [["Indicatore", "Valore"]],
    body: kpiData,
    theme: "striped",
    headStyles: { fillColor: [66, 66, 66] },
  });

  // Critical Positions Table
  doc.setFontSize(14);
  doc.text(
    "Top 20 Posizioni Critiche",
    14,
    doc.lastAutoTable.finalY + 15,
  );

  const criticalData = [...data]
    .sort((a, b) => b.prioritaIntervento - a.prioritaIntervento)
    .slice(0, 20)
    .map((r) => [
      (r.clienteRinnovo || "").substring(0, 20),
      (r.prodottoRinnovo || "").substring(0, 15),
      formatCurrency(r.premioAnnualeScadenza),
      formatCurrency(r.premioAnnualeRinnovo),
      formatPercent(r.deltaPercentualeCalcolato),
      String(r.prioritaIntervento),
    ]);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 20,
    head: [
      [
        "Cliente",
        "Prodotto",
        "Premio Scad.",
        "Premio Rinn.",
        "Delta %",
        "Priorità",
      ],
    ],
    body: criticalData,
    theme: "grid",
    headStyles: { fillColor: [220, 53, 69] },
  });

  doc.save(`report_rinnovi_${format(new Date(), "yyyyMMdd")}.pdf`);
};
